import { useState } from "react";
import {
  MagnifyingGlass,
  Clock,
  Compass,
  Users,
  Tag,
  CheckSquare,
  Hourglass,
  HourglassHigh,
  X,
  CaretDown,
} from "@phosphor-icons/react";
import { DEFAULT_FILTERS } from "../lib/unifyRoadmapItems";

/**
 * RoadmapFiltersBarV2 — unified filter bar for the Phase 12E master-detail
 * layout. Replaces the cycle-5c+23 filter bar.
 *
 * Filter dimensions:
 *   - search:     free-text against item.search_text
 *   - status:     open | closed | all          (default: open)
 *   - type:       all | promise | workbook | safety_net | feature | activity | ledger
 *   - tier:       all | current/Now | next/Next | future/Later  (P-tier auto-grouped)
 *   - agent:      all | nb | ec | ei | qb | ri | mp
 *   - effort:     all | quick-win | short-focus | long-focus | challenging
 *   - waiting_on: all | owner | agent | both | done
 *   - sort:       mp_rec | tier | recency  (cycle-5c+25-fix8: keep MP rec per Owner Q4)
 */

const STATUS_OPTIONS = [
  { value: "open",    label: "Open" },
  { value: "decided", label: "Decided" },
  { value: "closed",  label: "Closed" },
  { value: "all",     label: "All" },
];
const TYPE_OPTIONS = [
  { value: "all",        label: "All types" },
  { value: "promise",    label: "Promises" },
  { value: "workbook",   label: "Workbooks" },
  { value: "safety_net", label: "Safety-nets" },
  { value: "feature",    label: "Features" },
  { value: "activity",   label: "Activity" },
  { value: "ledger",     label: "Decided" },
];
const TIER_OPTIONS = [
  { value: "all",     label: "All tiers" },
  { value: "current", label: "Now (P0/P1)" },
  { value: "next",    label: "Next (P2/P3)" },
  { value: "future",  label: "Later (P4/P5)" },
];
const AGENT_OPTIONS = [
  { value: "all", label: "All agents" },
  { value: "nb",  label: "NB" },
  { value: "ec",  label: "EC" },
  { value: "ei",  label: "EI" },
  { value: "qb",  label: "QB" },
  { value: "ri",  label: "RI" },
  { value: "mp",  label: "MP" },
];
const EFFORT_OPTIONS = [
  { value: "all",          label: "Any time" },
  { value: "quick-win",    label: "Quick (≤5m)" },
  { value: "short-focus",  label: "Short (15-30m)" },
  { value: "long-focus",   label: "Long (1-2h)" },
  { value: "challenging",  label: "Hard (>2h)" },
];
const WAITING_OPTIONS = [
  { value: "all",   label: "All" },
  { value: "owner", label: "You" },
  { value: "agent", label: "Agents" },
  { value: "both",  label: "Both" },
  { value: "done",  label: "Done" },
];
const SORT_OPTIONS = [
  { value: "mp_rec", label: "MP rec" },
  { value: "tier",   label: "Tier" },
  { value: "recent", label: "Recent" },
  // cycle-5c+25-fix20 (Phase 14 / Batch 3) — Owner Q4=(c) stale-first sort.
  { value: "stale",  label: "Stale first" },
];

// cycle-5c+25-fix20 — Owner Q4=(c) staleness filter presets.
const STALE_OPTIONS = [
  { value: 0,  label: "Any age" },
  { value: 7,  label: "7+ days" },
  { value: 14, label: "14+ days" },
  { value: 30, label: "30+ days" },
  { value: 60, label: "60+ days" },
];

function Select({ icon: Icon, label, value, options, onChange, testId, defaultValue }) {
  const isActive = defaultValue !== undefined
    ? value !== defaultValue
    : value !== "all" && value !== "open" && value !== "mp_rec";
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <Icon size={13} style={{ color: "hsl(var(--muted-foreground))" }} />
      <span
        className="text-mono"
        style={{
          fontSize: "0.6rem",
          color: "hsl(var(--muted-foreground))",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 700,
        }}
      >
        {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            // If the option set holds numeric values, convert back to Number.
            const matched = options.find((o) => String(o.value) === raw);
            onChange(matched ? matched.value : raw);
          }}
          className="appearance-none rounded-md border pl-2 pr-6 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{
            background: isActive
              ? "hsl(var(--primary) / 0.10)"
              : "hsl(var(--background))",
            color: "hsl(var(--foreground))",
            borderColor: isActive
              ? "hsl(var(--primary) / 0.45)"
              : "hsl(var(--border))",
            fontSize: "0.75rem",
            fontWeight: 500,
          }}
          data-testid={testId}
        >
          {options.map((o) => (
            <option key={String(o.value)} value={o.value}>{o.label}</option>
          ))}
        </select>
        <CaretDown
          size={10}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "hsl(var(--muted-foreground))" }}
        />
      </div>
    </div>
  );
}

export default function RoadmapFiltersBarV2({ filters, setFilters }) {
  const [searchLocal, setSearchLocal] = useState(filters.search || "");

  const update = (key, value) => setFilters({ ...filters, [key]: value });

  const clearAll = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setSearchLocal("");
  };

  const activeCount = Object.entries(filters).reduce((n, [k, v]) => {
    return n + (v !== DEFAULT_FILTERS[k] ? 1 : 0);
  }, 0);

  return (
    <section
      className="surface-card sticky top-0 z-10 mb-4 p-2 sm:p-3"
      style={{ background: "hsl(var(--background))" }}
      data-testid="roadmap-filters-bar-v2"
    >
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-1.5 flex-1 min-w-[160px] sm:min-w-[200px]">
          <MagnifyingGlass size={13} style={{ color: "hsl(var(--muted-foreground))" }} />
          <input
            type="text"
            value={searchLocal}
            onChange={(e) => {
              setSearchLocal(e.target.value);
              update("search", e.target.value);
            }}
            placeholder="Search title, ref, agent..."
            className="flex-1 rounded-md border px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{
              background: "hsl(var(--background))",
              borderColor: searchLocal ? "hsl(var(--primary) / 0.45)" : "hsl(var(--border))",
              color: "hsl(var(--foreground))",
            }}
            data-testid="roadmap-filter-search"
          />
        </div>
      </div>
      {/* cycle-5c+25-fix22: on narrow Android viewports (<sm 640px) the 7 filter
          selects + clear button overflow the row. Switch to a horizontal scroll
          container so every filter stays reachable with a swipe right. Larger
          viewports keep the original flex-wrap layout. */}
      <div className="flex items-center gap-2 sm:gap-3 mt-2 overflow-x-auto sm:flex-wrap sm:overflow-x-visible -mx-2 px-2 sm:mx-0 sm:px-0 pb-1 sm:pb-0 scrollbar-thin">
        <Select icon={CheckSquare} label="Status"  value={filters.status}     options={STATUS_OPTIONS}  onChange={(v) => update("status", v)}     testId="roadmap-filter-status" />
        <Select icon={Tag}         label="Type"    value={filters.type}       options={TYPE_OPTIONS}    onChange={(v) => update("type", v)}       testId="roadmap-filter-type" />
        <Select icon={Compass}     label="Tier"    value={filters.tier}       options={TIER_OPTIONS}    onChange={(v) => update("tier", v)}       testId="roadmap-filter-tier" />
        <Select icon={Users}       label="Agent"   value={filters.agent}      options={AGENT_OPTIONS}   onChange={(v) => update("agent", v)}      testId="roadmap-filter-agent" />
        <Select icon={Clock}       label="Time"    value={filters.effort}     options={EFFORT_OPTIONS}  onChange={(v) => update("effort", v)}     testId="roadmap-filter-effort" />
        <Select icon={Hourglass}   label="Waiting" value={filters.waiting_on} options={WAITING_OPTIONS} onChange={(v) => update("waiting_on", v)} testId="roadmap-filter-waiting" />
        <Select icon={HourglassHigh} label="Stale"  value={filters.stale_min_days} options={STALE_OPTIONS}   onChange={(v) => update("stale_min_days", v)} testId="roadmap-filter-stale" defaultValue={0} />
        <Select icon={Compass}     label="Sort"    value={filters.sort}       options={SORT_OPTIONS}    onChange={(v) => update("sort", v)}       testId="roadmap-filter-sort" />
        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-mono rounded-md px-2 py-1 shrink-0"
            style={{
              fontSize: "0.65rem",
              color: "hsl(var(--muted-foreground))",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
            }}
            data-testid="roadmap-filter-clear-all"
          >
            <X size={10} />
            Clear ({activeCount})
          </button>
        )}
      </div>
    </section>
  );
}
